import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import type { ShiftCode, ShiftCodeSegment } from "@/services/schedule/shiftCodeService";
import { SplitShiftSegmentBuilder } from "./SplitShiftSegmentBuilder";

const shiftCodeSchema = z.object({
  code: z.string().min(1, "Code is required").max(10, "Code must be 10 characters or less"),
  name: z.string().min(1, "Name is required").max(50, "Name must be 50 characters or less"),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  default_hours: z.number().min(0).max(24).optional(),
  color: z.string().regex(/^#[0-9A-F]{6}$/i, "Must be a valid hex color"),
  category: z.string(),
  is_active: z.boolean(),
  is_split_shift: z.boolean().optional(),
  split_segments: z.array(z.object({
    label: z.string().optional(),
    start_time: z.string().min(1, "Start time is required"),
    end_time: z.string().min(1, "End time is required"),
  })).nullable().optional(),
});

type ShiftCodeFormValues = z.infer<typeof shiftCodeSchema>;

interface ShiftCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: ShiftCodeFormValues) => void;
  shiftCode?: ShiftCode | null;
  isLoading?: boolean;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f97316', // orange
];

export function ShiftCodeDialog({
  open,
  onOpenChange,
  onSubmit,
  shiftCode,
  isLoading,
}: ShiftCodeDialogProps) {
  const isSystemCode = shiftCode?.is_system === true;

  const form = useForm<ShiftCodeFormValues>({
    resolver: zodResolver(shiftCodeSchema),
    defaultValues: {
      code: '',
      name: '',
      start_time: '',
      end_time: '',
      default_hours: undefined,
      color: '#3b82f6',
      category: 'work',
      is_active: true,
      is_split_shift: false,
      split_segments: null,
    },
  });

  const selectedCategory = form.watch('category');
  const isSplitShift = selectedCategory === 'work_split_shift';
  const showTimeFields = (selectedCategory === 'work' || selectedCategory === 'training') && !isSplitShift;

  useEffect(() => {
    if (shiftCode) {
      form.reset({
        code: shiftCode.code,
        name: shiftCode.name,
        start_time: shiftCode.start_time || '',
        end_time: shiftCode.end_time || '',
        default_hours: shiftCode.default_hours || undefined,
        color: shiftCode.color,
        category: shiftCode.category,
        is_active: shiftCode.is_active,
        is_split_shift: shiftCode.is_split_shift || false,
        split_segments: shiftCode.split_segments || null,
      });
    } else {
      form.reset({
        code: '',
        name: '',
        start_time: '',
        end_time: '',
        default_hours: undefined,
        color: '#3b82f6',
        category: 'work',
        is_active: true,
        is_split_shift: false,
        split_segments: null,
      });
    }
  }, [shiftCode, form]);

  // Clear time fields when switching categories
  useEffect(() => {
    if (selectedCategory === 'off' || selectedCategory === 'break') {
      form.setValue('start_time', '');
      form.setValue('end_time', '');
      form.setValue('is_split_shift', false);
      form.setValue('split_segments', null);
    } else if (selectedCategory === 'work_split_shift') {
      form.setValue('is_split_shift', true);
      form.setValue('start_time', '');
      form.setValue('end_time', '');
      // Initialize with 2 empty segments if none exist
      const currentSegments = form.getValues('split_segments');
      if (!currentSegments || currentSegments.length === 0) {
        const initialSegments = [
          { start_time: '09:00:00', end_time: '12:00:00' },
          { start_time: '14:00:00', end_time: '17:00:00' },
        ] as ShiftCodeSegment[];
        form.setValue('split_segments', initialSegments);
      }
    } else {
      form.setValue('is_split_shift', false);
      form.setValue('split_segments', null);
    }
  }, [selectedCategory, form]);

  const handleSubmit = (values: ShiftCodeFormValues) => {
    // Transform empty arrays to null before submission
    const transformedValues = {
      ...values,
      split_segments: values.split_segments && Array.isArray(values.split_segments) && values.split_segments.length > 0 
        ? values.split_segments 
        : null,
    };
    onSubmit(transformedValues);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {shiftCode ? 'Edit Shift Code' : 'Create Shift Code'}
            {isSystemCode && (
              <Badge variant="outline" className="ml-2 text-xs">System</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSystemCode
              ? 'You can customize the name and color of this system shift code.'
              : shiftCode
                ? 'Update the shift code details below.'
                : 'Add a new shift code for your roster planning.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="D, ES, M1"
                        maxLength={10}
                        {...field}
                        className="uppercase"
                        disabled={isSystemCode}
                      />
                    </FormControl>
                    {isSystemCode && (
                      <FormDescription>System code cannot be changed</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSystemCode}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="work">Work</SelectItem>
                        <SelectItem value="work_split_shift">Work Split Shift</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                        <SelectItem value="break">Break</SelectItem>
                        <SelectItem value="training">Training</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Day Shift, Early Start" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isSystemCode && (
              isSplitShift ? (
                <FormField
                  control={form.control}
                  name="split_segments"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <SplitShiftSegmentBuilder
                          segments={(field.value || []) as ShiftCodeSegment[]}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : (
                <div className={showTimeFields ? "grid grid-cols-3 gap-4" : "grid grid-cols-1 gap-4"}>
                  {showTimeFields && (
                    <>
                      <FormField
                        control={form.control}
                        name="start_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="end_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>End Time</FormLabel>
                            <FormControl>
                              <Input type="time" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  <FormField
                    control={form.control}
                    name="default_hours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Hours</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            max="24"
                            placeholder="8"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormDescription>
                          {showTimeFields ? 'Optional: override calculated hours' : 'Total hours for this shift'}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )
            )}

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <div className="flex gap-2 items-center">
                    <FormControl>
                      <Input type="color" {...field} className="w-20 h-10" />
                    </FormControl>
                    <div className="flex gap-1">
                      {DEFAULT_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          className="w-6 h-6 rounded border-2 border-border hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => field.onChange(color)}
                        />
                      ))}
                    </div>
                  </div>
                  <FormDescription>Choose a color for visual identification</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isSystemCode && (
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <FormDescription>
                        Inactive shift codes won't appear in the roster planner
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : shiftCode ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
